import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useComunicacao } from '../../contexts/ComunicacaoContext';
import comunicacaoService from '../../services/comunicacaoService';
import BarraFormatacao from '../common/BarraFormatacao';
import { Anexo } from '../../types/comunicacao';

interface ModalEditarTopicoProps {
  topicoId: string;
  onClose: () => void;
}

const ModalEditarTopico: React.FC<ModalEditarTopicoProps> = ({ topicoId, onClose }) => {
  const { topicos, canalSelecionado, topicoSelecionado, carregarTopicos, selecionarTopico } = useComunicacao();
  const [titulo, setTitulo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [anexosExistentes, setAnexosExistentes] = useState<Anexo[]>([]);
  const [anexosParaRemover, setAnexosParaRemover] = useState<string[]>([]);
  const [novosArquivos, setNovosArquivos] = useState<File[]>([]);
  const [carregandoAnexos, setCarregandoAnexos] = useState(true);

  useEffect(() => {
    const topico = topicos.find(t => t.id === topicoId);
    if (topico) {
      setTitulo(topico.titulo);
      setMensagem(topico.mensagem);
    }
  }, [topicoId, topicos]);

  useEffect(() => {
    comunicacaoService.listarAnexosTopico(topicoId)
      .then(setAnexosExistentes)
      .catch(() => {})
      .finally(() => setCarregandoAnexos(false));
  }, [topicoId]);

  const marcarParaRemover = (anexoId: string) => {
    setAnexosParaRemover(prev => [...prev, anexoId]);
    setAnexosExistentes(prev => prev.filter(a => a.id !== anexoId));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setNovosArquivos(prev => [...prev, ...files]);
      e.target.value = '';
    }
  };

  const removerNovoArquivo = (index: number) => {
    setNovosArquivos(prev => prev.filter((_, i) => i !== index));
  };

  const formatarTamanho = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim() || !mensagem.trim()) return;
    setErro('');

    try {
      setSalvando(true);

      // 1. Salva título e mensagem direto no serviço (sem toast prematuro)
      await comunicacaoService.editarTopico(topicoId, { titulo: titulo.trim(), mensagem: mensagem.trim() });

      // 2. Remove anexos marcados
      for (const anexoId of anexosParaRemover) {
        await comunicacaoService.removerAnexoTopico(anexoId);
      }

      // 3. Faz upload dos novos arquivos
      for (const arquivo of novosArquivos) {
        const formData = new FormData();
        formData.append('file', arquivo);
        await comunicacaoService.uploadAnexoTopico(topicoId, arquivo);
      }

      // 4. Recarrega contexto com tudo já salvo
      if (canalSelecionado) {
        await carregarTopicos(canalSelecionado.id);
      }
      if (topicoSelecionado?.id === topicoId) {
        await selecionarTopico(topicoId);
      }

      toast.success('Tópico atualizado com sucesso!');
      onClose();
    } catch (error: any) {
      console.error('Erro ao editar tópico:', error);
      setErro(error?.response?.data?.message || 'Erro ao salvar alterações. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-800">Editar Tópico</h2>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-y-auto min-h-0 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Digite o título do tópico"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent"
              required
            />
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Mensagem *</label>
            <BarraFormatacao
              valor={mensagem}
              onChange={setMensagem}
              placeholder="Digite sua mensagem"
              minHeight={150}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Anexos</label>

            {carregandoAnexos ? (
              <p className="text-sm text-gray-400">Carregando anexos...</p>
            ) : (
              <>
                {anexosExistentes.length > 0 && (
                  <div className="mb-3 space-y-2">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Anexos atuais</p>
                    {anexosExistentes.map(anexo => (
                      <div key={anexo.id} className="flex items-center justify-between bg-gray-50 p-2 rounded border border-gray-200">
                        <div className="flex items-center gap-2 min-w-0">
                          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{anexo.nome_original || anexo.nome_arquivo}</p>
                            <p className="text-xs text-gray-400">{formatarTamanho(anexo.tamanho)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => marcarParaRemover(anexo.id)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                          title="Remover anexo"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Adicionar novos anexos</p>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">Máximo 50MB por arquivo</p>
                </div>

                {novosArquivos.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {novosArquivos.map((arquivo, index) => (
                      <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded border border-blue-200">
                        <div className="flex items-center gap-2 min-w-0">
                          <svg className="w-5 h-5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{arquivo.name}</p>
                            <p className="text-xs text-gray-400">{formatarTamanho(arquivo.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removerNovoArquivo(index)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {erro && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
              {erro}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              disabled={salvando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!titulo.trim() || !mensagem.trim() || salvando}
              className="px-4 py-2 bg-bravo-primary hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEditarTopico;
